'use client';

import { useEffect, useState } from 'react';
import { useOrderStore, type OrderItem } from '@/store/order-store';

export interface CartApi {
  items: OrderItem[];
  itemCount: number;
  total: number;
  addItem: (item: Omit<OrderItem, 'quantity'> & { quantity?: number }) => void;
  removeItem: (productId: string, variantId: string) => void;
  updateQuantity: (productId: string, variantId: string, quantity: number) => void;
  clearCart: () => void;
}

/**
 * The one place every screen (mobile or desktop) goes to read/mutate the cart.
 * Swapping the underlying store implementation later only touches this file.
 *
 * SSR/hydration-safe: the persisted cart lives in localStorage, which the
 * server never sees. Until the component mounts every consumer reads an
 * empty cart (exactly what the server rendered), so totals and item counts
 * never mismatch between server HTML and the first client render.
 */
export function useCart(): CartApi {
  // Mounted flag (not the zustand persist API) so this also holds during
  // static prerender, where the persist API is unavailable.
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    setHydrated(true);
  }, []);

  const items = useOrderStore((s) => s.items);
  const addItem = useOrderStore((s) => s.addItem);
  const removeItem = useOrderStore((s) => s.removeItem);
  const updateQuantity = useOrderStore((s) => s.updateQuantity);
  const clearCart = useOrderStore((s) => s.clearOrder);
  const totalItems = useOrderStore((s) => s.totalItems);
  const totalPrice = useOrderStore((s) => s.totalPrice);

  if (!hydrated) {
    return {
      items: [],
      itemCount: 0,
      total: 0,
      addItem,
      removeItem,
      updateQuantity,
      clearCart,
    };
  }

  return {
    items,
    itemCount: totalItems(),
    total: totalPrice(),
    addItem,
    removeItem,
    updateQuantity,
    clearCart,
  };
}
